import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { ChartBlock } from '../components/AnalysisChart';
import { MarkdownView } from '../components/MarkdownView';
import { AnalysisService, type Analysis, type AnalysisSnapshot } from '../services/analysis-service';

/** One analysis at its own address.
 *
 * Separate from the list dialog so an analysis can be linked to, revisited
 * and cited. A conclusion about someone's retirement that exists only inside
 * a modal is hard to come back to and impossible to point anyone at.
 */
export function AnalysisDetailPage({ id }: { id: string }) {
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [snapshot, setSnapshot] = useState<AnalysisSnapshot | null>(null);
  const [takenAt, setTakenAt] = useState<string>('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let live = true;
    AnalysisService.get(id)
      .then((r) => {
        if (!live) return;
        setAnalysis(r.analysis);
        setSnapshot(r.snapshot ?? null);
        setTakenAt(r.snapshot_taken_at ?? '');
      })
      .catch(() => live && setError('That analysis could not be loaded.'));
    return () => { live = false; };
  }, [id]);

  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  if (!analysis) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Button component={Link} to="/analysis" startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
        All analyses
      </Button>

      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4">{analysis.title || 'Analysis'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(analysis.created_at).toLocaleString()}
            {analysis.model ? ` · ${analysis.model}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip size="small" label={analysis.source === 'mcp' ? 'Claude (MCP)' : 'LLM key'} />
          <Button size="small" startIcon={<ContentCopyIcon />}
            onClick={() => { void navigator.clipboard.writeText(window.location.href); setCopied(true); }}>
            {copied ? 'Copied' : 'Copy link'}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, my: 2, bgcolor: 'action.hover' }}>
        <Typography variant="body2"><strong>Asked:</strong> {analysis.prompt}</Typography>
      </Paper>

      {snapshot && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Based on the portfolio as it stood{takenAt ? ` on ${new Date(takenAt).toLocaleString()}` : ''}
            </Typography>
            <Stack direction="row" spacing={4} flexWrap="wrap">
              <Stat label="Net worth" value={money(snapshot.net_worth, snapshot.base_currency)}
                help="Every account's balance converted to the household's base currency. Derived from ledger entries, so it matches the Net Worth page exactly." />
              <Stat label="Accounts" value={String(snapshot.accounts?.length ?? 0)}
                help="Every account in the household at the moment this analysis ran, including any since closed or renamed." />
              <Stat label="Positions" value={String(snapshot.positions?.length ?? 0)}
                help="Individual holdings as the brokerage reported them. One symbol held in two accounts counts twice here, but is pooled when concentration is measured." />
              <Stat label="Trailing 12m income"
                value={money(snapshot.trailing_12m_income, snapshot.base_currency)}
                help="Dividends, interest and distributions actually received in the last 12 months, net of withholding tax. Rows imported before a dated FX rate was available are counted unconverted, which understates foreign income." />
            </Stack>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent>
          <MarkdownView text={analysis.result_md} />
          {(analysis.charts ?? []).length > 0 && <Divider sx={{ my: 3 }} />}
          {(analysis.charts ?? []).map((c, i) => <ChartBlock key={i} spec={c} />)}
        </CardContent>
      </Card>
    </Box>
  );
}

/** A figure with the explanation of what it actually measures on hover.
 *
 * Every number on this page is one definition away from being misread --
 * "income" that is gross or net, "positions" that are pooled or not. The
 * tooltip carries the definition so the figure cannot be quietly
 * misinterpreted. */
function Stat({ label, value, help }: { label: string; value: string; help?: string }) {
  return (
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {help && (
          <Tooltip title={help} arrow enterTouchDelay={0} leaveTouchDelay={6000}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'help' }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}

function money(v: string | undefined, currency: string) {
  const n = Number(v ?? 0);
  if (!Number.isFinite(n)) return String(v ?? '');
  return new Intl.NumberFormat('en-CA', {
    style: 'currency', currency: currency || 'CAD', maximumFractionDigits: 0,
  }).format(n);
}
